import { Injectable, NgZone } from '@angular/core';
import { AngularFirestore } from 'angularfire2/firestore';
import { AngularFireDatabase } from 'angularfire2/database';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export type ConnectionQuality = 'online' | 'weak' | 'offline';

export interface ConnectionState {
	quality: ConnectionQuality;
	/** Ida y vuelta (ms) del último sondeo a Firestore; null si falló. */
	latency: number | null;
	/** Escrituras aceptadas localmente que el servidor todavía no confirma. */
	pendingWrites: number;
	/** Quedaron escrituras en cola de una sesión anterior (recarga estando offline). */
	pendingFromPreviousSession: boolean;
	/** Hay conexión y todavía quedan escrituras por confirmar. */
	syncing: boolean;
	/** navigator.onLine: false = el dispositivo no tiene ninguna red. */
	browserOnline: boolean;
	/** `.info/connected` de Realtime Database: el socket real con Firebase. */
	firebaseConnected: boolean | null;
	/**
	 * Firestore logró activar la copia local en IndexedDB. false = este
	 * navegador no la soporta o la bloqueó (modo privado, otra pestaña sin
	 * synchronizeTabs): las escrituras siguen en cola en memoria, pero se
	 * pierden si se cierra la pestaña. null = todavía no se sabe.
	 */
	persistence: boolean | null;
	/** Desde cuándo está en esta calidad. */
	since: Date;
}

export interface WriteFailure {
	label: string;
	error: any;
}

// Respuesta de ~60 bytes con CORS, sin autenticación, en la misma
// infraestructura de Google que atiende a Firestore. Un portal cautivo
// devuelve HTML sin CORS y el fetch rechaza, así que también lo detecta.
const PROBE_URL = 'https://www.googleapis.com/discovery/v1/apis?name=firestore&preferred=true&fields=items/name';
// Sondeo: cada cuánto se mide la conexión según el estado actual.
const PROBE_INTERVAL_OK_MS = 15000;
const PROBE_INTERVAL_DEGRADED_MS = 5000;
// Un sondeo que tarda más que esto cuenta como fallido.
const PROBE_TIMEOUT_MS = 6000;
// Un sondeo que tarda más que esto cuenta como señal débil.
const WEAK_LATENCY_MS = 2000;
// Sondeos seguidos que hacen falta para cambiar de estado (evita parpadeo).
const SLOW_PROBES_FOR_WEAK = 2;
const FAILED_PROBES_FOR_OFFLINE = 2;

/**
 * Vigila la conexión con Firebase y lleva la cuenta de lo que falta por
 * sincronizar. Es la única pieza que decide cuándo Firestore trabaja en
 * modo offline:
 *
 * - `navigator.onLine` responde al instante cuando el dispositivo pierde la
 *   red, pero dice "online" aunque el wifi no tenga internet.
 * - El sondeo HTTP (una respuesta diminuta de la API de Google que sirve
 *   Firestore) mide la latencia real hasta Google. Un portal cautivo o un
 *   wifi sin salida fallan aquí aunque el navegador diga online.
 * - `.info/connected` de Realtime Database confirma el socket con Firebase y
 *   dispara un sondeo inmediato cuando cambia.
 *
 * Cuando se declara offline se llama a `disableNetwork()` para que Firestore
 * responda desde caché de inmediato en vez de esperar ~10 s a que sus
 * streams fallen; al recuperar, `enableNetwork()` vacía la cola.
 */
@Injectable({
	providedIn: 'root'
})
export class ConnectivityService {

	private state: ConnectionState = {
		quality: 'online',
		latency: null,
		pendingWrites: 0,
		pendingFromPreviousSession: false,
		syncing: false,
		browserOnline: typeof navigator !== 'undefined' ? navigator.onLine !== false : true,
		firebaseConnected: null,
		persistence: null,
		since: new Date(),
	};

	private stateSubject = new BehaviorSubject<ConnectionState>(this.state);
	/** Estado completo; emite cada vez que cambia cualquier campo. */
	readonly state$: Observable<ConnectionState> = this.stateSubject.asObservable();
	/** Solo la calidad (online | weak | offline), sin repetidos. */
	readonly quality$: Observable<ConnectionQuality> = this.state$.pipe(
		map(s => s.quality),
		distinctUntilChanged()
	);
	/** Se emite cuando, tras volver la conexión, Firestore confirmó toda la cola. */
	readonly synced$ = new Subject<void>();
	/** Escrituras que el servidor rechazó (reglas, datos inválidos); no es un problema de red. */
	readonly writeFailed$ = new Subject<WriteFailure>();

	private probeTimer: any = null;
	private probeInFlight = false;
	/** Pidieron un sondeo mientras otro estaba en curso: se repite al terminar. */
	private probeRequested = false;
	private consecutiveSlow = 0;
	private consecutiveFailed = 0;
	private firestoreNetworkEnabled = true;
	private started = false;

	constructor(
		private afs: AngularFirestore,
		private afdb: AngularFireDatabase,
		private zone: NgZone
	) {
		// Para soporte: en la consola del navegador, `__connectivity.current`
		// muestra el estado y `__connectivity.probeNow()` fuerza un sondeo.
		if (typeof window !== 'undefined') (window as any).__connectivity = this;
		this.start();
	}

	get current(): ConnectionState {
		return this.state;
	}

	get isOffline(): boolean {
		return this.state.quality === 'offline';
	}

	/**
	 * true cuando conviene leer de caché en vez de esperar al servidor: sin
	 * conexión o con señal débil. Los datos que el monitor mantiene con
	 * listeners en vivo (bricks, presentaciones, órdenes) ya están en caché.
	 */
	get preferCache(): boolean {
		return this.state.quality !== 'online';
	}

	/**
	 * Registra una escritura a Firestore para contarla como pendiente hasta que
	 * el servidor la confirme. Devuelve la misma promesa (ya con el conteo).
	 */
	trackWrite<T>(write: Promise<T>, label: string): Promise<T> {
		this.patch({ pendingWrites: this.state.pendingWrites + 1 });
		return write.then(
			value => {
				this.patch({ pendingWrites: Math.max(0, this.state.pendingWrites - 1) });
				return value;
			},
			error => {
				this.patch({ pendingWrites: Math.max(0, this.state.pendingWrites - 1) });
				console.error(`[connectivity] escritura rechazada por el servidor (${label})`, error);
				this.zone.run(() => this.writeFailed$.next({ label, error }));
				throw error;
			}
		);
	}

	/** Fuerza un sondeo ahora (por ejemplo, al abrir una pantalla crítica). */
	probeNow() {
		this.scheduleProbe(0);
	}

	// ─────────────────────────────────────────────────────────────────────
	//  Arranque y fuentes de señal
	// ─────────────────────────────────────────────────────────────────────

	private start() {
		if (this.started || typeof window === 'undefined') return;
		this.started = true;

		this.zone.runOutsideAngular(() => {
			window.addEventListener('online', () => {
				this.patch({ browserOnline: true });
				this.scheduleProbe(0);
			});
			window.addEventListener('offline', () => {
				this.patch({ browserOnline: false });
				this.evaluate();
			});
			// Al volver a la pestaña medimos enseguida; no confiamos en un
			// estado calculado mientras estuvo en segundo plano.
			document.addEventListener('visibilitychange', () => {
				if (document.visibilityState === 'visible') this.scheduleProbe(0);
			});

			const netInfo = (navigator as any).connection;
			if (netInfo && typeof netInfo.addEventListener === 'function') {
				netInfo.addEventListener('change', () => this.scheduleProbe(0));
			}

			this.watchFirebaseSocket();
			this.watchPersistence();
			this.watchPreviousSessionQueue();
			this.scheduleProbe(0);
		});
	}

	private watchPersistence() {
		this.afs.persistenceEnabled$.subscribe(enabled => {
			this.patch({ persistence: enabled });
			if (!enabled) {
				console.warn('[connectivity] Firestore no pudo activar la copia local (IndexedDB): sin conexión solo se conserva lo que esté en memoria y la cola se pierde al cerrar la pestaña.');
			}
		});
	}

	private watchFirebaseSocket() {
		try {
			this.afdb.database.ref('.info/connected').on('value', snap => {
				const connected = snap.val() === true;
				const changed = this.state.firebaseConnected !== connected;
				this.patch({ firebaseConnected: connected });
				if (changed) this.scheduleProbe(0);
			});
		} catch (error) {
			console.warn('[connectivity] no se pudo observar .info/connected', error);
		}
	}

	/**
	 * Si la app se recargó estando offline, Firestore ya trae en IndexedDB las
	 * escrituras que no alcanzó a enviar. No sabemos cuántas son, pero sí que
	 * existen hasta que `waitForPendingWrites` resuelva.
	 */
	private watchPreviousSessionQueue(attempt = 0) {
		let settled = false;
		const wait = this.afs.firestore.waitForPendingWrites();
		wait.then(() => {
			settled = true;
			const hadQueue = this.state.pendingFromPreviousSession;
			this.patch({ pendingFromPreviousSession: false });
			if (hadQueue) this.zone.run(() => this.synced$.next());
		}).catch(() => {
			settled = true;
			this.patch({ pendingFromPreviousSession: false });
			// Firestore rechaza la espera cuando cambia el usuario (al restaurar
			// la sesión guardada, por ejemplo); la cola es por usuario, así que
			// se vuelve a preguntar ya con el usuario correcto.
			if (attempt < 2) setTimeout(() => this.watchPreviousSessionQueue(attempt + 1), 2000);
		});
		// Si en 3 s no confirmó nada, hay cola heredada (o la conexión está
		// muy lenta; en ambos casos vale avisar que se está sincronizando).
		setTimeout(() => {
			if (!settled) this.patch({ pendingFromPreviousSession: true });
		}, 3000);
	}

	// ─────────────────────────────────────────────────────────────────────
	//  Sondeo de latencia
	// ─────────────────────────────────────────────────────────────────────

	private scheduleProbe(delayMs: number) {
		if (this.probeTimer) clearTimeout(this.probeTimer);
		this.probeTimer = setTimeout(() => this.probe(), delayMs);
	}

	private async probe() {
		if (this.probeInFlight) {
			this.probeRequested = true;
			return;
		}
		this.probeInFlight = true;

		let latency: number | null = null;
		if (this.state.browserOnline) {
			latency = await this.measureLatency();
		}

		if (latency === null) {
			this.consecutiveFailed++;
			this.consecutiveSlow = 0;
		} else if (latency > WEAK_LATENCY_MS) {
			this.consecutiveFailed = 0;
			this.consecutiveSlow++;
		} else {
			this.consecutiveFailed = 0;
			this.consecutiveSlow = 0;
		}

		this.patch({ latency });
		this.evaluate();
		this.probeInFlight = false;

		if (this.probeRequested) {
			this.probeRequested = false;
			this.scheduleProbe(0);
			return;
		}
		const interval = this.state.quality === 'online' ? PROBE_INTERVAL_OK_MS : PROBE_INTERVAL_DEGRADED_MS;
		this.scheduleProbe(interval);
	}

	/** Milisegundos hasta recibir respuesta de Firestore, o null si no se pudo. */
	private measureLatency(): Promise<number | null> {
		const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
		const timeout = setTimeout(() => controller && controller.abort(), PROBE_TIMEOUT_MS);
		const started = performance.now();
		const url = `${PROBE_URL}&t=${Date.now()}`;
		return fetch(url, {
			method: 'GET',
			mode: 'cors',
			cache: 'no-store',
			credentials: 'omit',
			signal: controller ? controller.signal : undefined
		}).then(() => {
			return Math.round(performance.now() - started);
		}).catch(() => {
			return null;
		}).then(result => {
			clearTimeout(timeout);
			return result;
		});
	}

	/** true si el navegador reporta una red tipo 2G (Chrome/Android; Safari no lo expone). */
	private slowByNetworkInformation(): boolean {
		const netInfo = (navigator as any).connection;
		if (!netInfo) return false;
		const type = netInfo.effectiveType;
		return type === 'slow-2g' || type === '2g';
	}

	// ─────────────────────────────────────────────────────────────────────
	//  Decisión de estado
	// ─────────────────────────────────────────────────────────────────────

	private evaluate() {
		const wasOffline = this.state.quality === 'offline';
		let quality: ConnectionQuality;
		if (!this.state.browserOnline) {
			quality = 'offline';
		} else if (this.consecutiveFailed >= FAILED_PROBES_FOR_OFFLINE) {
			quality = 'offline';
		} else if (wasOffline && this.consecutiveFailed > 0) {
			// De "sin conexión" solo se sale con un sondeo exitoso; un fallo
			// más no lo convierte en "señal débil".
			quality = 'offline';
		} else if (this.consecutiveFailed > 0) {
			// Un fallo aislado todavía no es "sin conexión", pero sí aviso.
			quality = 'weak';
		} else if (this.consecutiveSlow >= SLOW_PROBES_FOR_WEAK || this.slowByNetworkInformation()) {
			quality = 'weak';
		} else {
			quality = 'online';
		}

		const previous = this.state.quality;
		if (quality !== previous) {
			console.info(`[connectivity] ${previous} → ${quality}`, { latency: this.state.latency, pendingWrites: this.state.pendingWrites });
			this.patch({ quality, since: new Date() });
			this.applyFirestoreNetwork(quality);
			if (previous === 'offline') this.announceWhenSynced();
		}
	}

	private applyFirestoreNetwork(quality: ConnectionQuality) {
		const wantEnabled = quality !== 'offline';
		if (wantEnabled === this.firestoreNetworkEnabled) return;
		this.firestoreNetworkEnabled = wantEnabled;
		const toggle = wantEnabled
			? this.afs.firestore.enableNetwork()
			: this.afs.firestore.disableNetwork();
		toggle.catch(error => console.warn('[connectivity] no se pudo cambiar la red de Firestore', error));
	}

	private announceWhenSynced() {
		this.afs.firestore.waitForPendingWrites()
			.then(() => this.zone.run(() => {
				this.patch({ pendingFromPreviousSession: false });
				this.synced$.next();
			}))
			.catch(() => { });
	}

	private patch(changes: Partial<ConnectionState>) {
		const next = { ...this.state, ...changes };
		next.syncing = next.quality !== 'offline' && (next.pendingWrites > 0 || next.pendingFromPreviousSession);
		const changed = Object.keys(next).some(key => next[key] !== this.state[key]);
		this.state = next;
		if (changed) this.zone.run(() => this.stateSubject.next(this.state));
	}
}
